import axios from "axios";

import { API_ROUTES } from "../api/ApiRoutes";
import { getPrivateInstance } from "../queries/_privateInstance";

/**
 * S3 image upload helper for Game Pass challenge icons / task images and the
 * player-facing theme assets.
 *
 * The backend flow has three steps:
 *   1. `POST /gamepass/v2/admin/s3/presign` with `{ key, content_type }`
 *      → `{ upload_url, public_url }`.
 *   2. `PUT <upload_url>` with the raw file bytes and the matching
 *      `Content-Type` header.
 *   3. Persist the result: challenges / tasks / seasons store the S3 `key` as
 *      `img_key`; the theme API stores the `public_url` (see
 *      {@link uploadGamePassThemeAsset}).
 *
 * Mirrors `casinoImageUpload.ts` (presign → PUT to S3) but uses the
 * Game-Pass-specific presign route and a caller-supplied object key.
 */

/** Subset of the presign response we depend on. The backend returns these
 *  at the top level; we still defensively unwrap a `data` envelope in case a
 *  future gateway wraps the body like the other Game Pass endpoints. */
export interface GamePassPresignResponse {
  upload_url?: string;
  public_url?: string;
}

type PresignEnvelope = GamePassPresignResponse & {
  data?: GamePassPresignResponse;
};

/** Folder segment under the `game-pass/` S3 prefix. Kept as a string union so
 *  a typo in a call site is a compile error rather than a stray S3 path. */
export type GamePassImageFolder = "challenges" | "tasks" | "theme" | "seasons";

/**
 * Build a previewable CloudFront URL from a stored `img_key`.
 *
 * The backend persists only the S3 object key (e.g.
 * `game-pass/challenges/123-abc-icon.png`) on challenges / tasks, not the full
 * URL. This reconstructs the same `public_url` the presign endpoint returns by
 * prefixing the configured CloudFront base (`REACT_APP_ADMIN_PANEL_CLOUDFRONT_URL`,
 * the same CDN the casino asset flow uses).
 *
 * Returns `null` when there's no key or no configured CDN base so callers can
 * fall back to the empty / "no preview" state instead of rendering a broken
 * `https://undefined/...` URL.
 */
export const buildGamePassImageUrl = (
  imgKey?: string | null,
): string | null => {
  if (!imgKey) return null;
  const base = process.env.REACT_APP_ADMIN_PANEL_CLOUDFRONT_URL;
  if (!base) return null;
  const trimmedBase = base.trim().replace(/\/+$/, "");
  const trimmedKey = imgKey.trim().replace(/^\/+/, "");
  if (!trimmedBase || !trimmedKey) return null;
  return `${trimmedBase}/${trimmedKey}`;
};

/** Lower-case + strip anything that isn't url-safe so the generated key never
 *  contains spaces / unicode that would need escaping in the S3 path. Falls
 *  back to `image` when the sanitised name is empty. */
const sanitizeFileName = (name: string): string => {
  const safe = name
    .toLowerCase()
    .replace(/[^a-z0-9.\-_]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return safe || "image";
};

/**
 * Build a unique, collision-resistant S3 object key for a Game Pass image,
 * e.g. `game-pass/tasks/1717327200000-9f3ax-my-banner.jpg`. The timestamp +
 * random suffix keeps two uploads of the same filename from clobbering one
 * another. Exported for unit testing.
 */
export const buildGamePassImageKey = (
  folder: GamePassImageFolder,
  file: File,
): string => {
  const safeName = sanitizeFileName(file.name || "image");
  const unique = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  return `game-pass/${folder}/${unique}-${safeName}`;
};

/**
 * Presign a given object `key` and PUT the file bytes to S3, resolving with the
 * presign response (which carries the `public_url`).
 *
 * The S3 `PUT` is sent with a bare axios call (no auth interceptor) so the
 * pre-signed URL's own signature is the only auth mechanism — attaching the
 * staff `Authorization` header can make S3 reject the request as a duplicate
 * auth mechanism. Only the `Content-Type` is forwarded, matching the
 * `content_type` we signed with.
 *
 * Shared by the challenge/task `img_key` flow ({@link uploadGamePassImage}) and
 * the theme `public_url` flow ({@link uploadGamePassThemeAsset}) so the
 * presign → PUT sequence has a single implementation.
 */
export const presignAndUploadToS3 = async ({
  key,
  file,
}: {
  key: string;
  file: File;
}): Promise<GamePassPresignResponse> => {
  const contentType = file.type || "application/octet-stream";

  const presignResponse = await getPrivateInstance().post<PresignEnvelope>(
    API_ROUTES.GAME_PASS_PRESIGN,
    { key, content_type: contentType },
  );

  const presigned = presignResponse?.data?.data ?? presignResponse?.data ?? {};
  const uploadUrl = presigned.upload_url;
  if (!uploadUrl) {
    throw new Error("Failed to generate a pre-signed upload URL");
  }

  await axios.put(uploadUrl, file, {
    headers: { "Content-Type": contentType },
  });

  return presigned;
};

/**
 * Presign + upload a single image to S3 and resolve with the S3 `key`
 * (the value the backend stores as `img_key` on challenges / tasks / seasons).
 */
export const uploadGamePassImage = async ({
  file,
  folder,
}: {
  file: File;
  folder: GamePassImageFolder;
}): Promise<string> => {
  const key = buildGamePassImageKey(folder, file);
  await presignAndUploadToS3({ key, file });
  return key;
};

/**
 * Resolve the `img_key` to send on a create/update, matching the challenge /
 * task dropzone contract:
 *   - a freshly picked `file` → upload once and return the new key
 *   - no new file but the persisted image was explicitly cleared
 *     (`persistedKey === null`) → `null` so the backend drops it
 *   - otherwise → `undefined` so the payload omits `img_key`
 */
export const resolveGamePassImgKeyForSave = async ({
  file,
  persistedKey,
  folder,
}: {
  file?: File;
  persistedKey?: string | null;
  folder: GamePassImageFolder;
}): Promise<string | null | undefined> => {
  if (file) return uploadGamePassImage({ file, folder });
  return persistedKey === null ? null : undefined;
};

/** File extension per allowed theme asset content type (image/jpeg|png|webp|
 *  gif|svg+xml). Used to stamp the theme key so the S3 object keeps a sensible
 *  extension even when the source filename has none. */
const THEME_ASSET_EXTENSION: Record<string, string> = {
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
  "image/gif": "gif",
  "image/svg+xml": "svg",
};

const resolveThemeAssetExtension = (file: File): string => {
  const byType = THEME_ASSET_EXTENSION[file.type];
  if (byType) return byType;
  const match = /\.([a-z0-9]+)$/i.exec(file.name ?? "");
  return (match?.[1] ?? "png").toLowerCase();
};

/**
 * Build the theme asset S3 key: `game-pass/theme/{slot}-{unix_ts}.{ext}`.
 *
 * This deliberately differs from {@link buildGamePassImageKey} (challenge/task
 * uses `{ts}-{rand}-{name}`): theme slots are a small fixed set, so the slot +
 * unix timestamp is unique enough and produces a readable, sluggable key. Every
 * save mints a fresh timestamped key — old objects are left orphaned (fine for
 * MVP, matching the backend's immutable-key expectation). Exported for tests.
 */
export const buildGamePassThemeAssetKey = (
  slot: string,
  file: File,
): string => {
  const unixTs = Math.floor(Date.now() / 1000);
  return `game-pass/theme/${slot}-${unixTs}.${resolveThemeAssetExtension(file)}`;
};

/** A valid theme asset value is a CDN URL under the `game-pass/` prefix — the
 *  theme API 422s anything else, so we validate the presign `public_url`
 *  before handing it back for the PATCH. */
const THEME_ASSET_URL_PATTERN = /^https:\/\/[^/]+\/game-pass\//i;

/**
 * Presign + upload a theme asset to S3 and resolve with its `public_url` —
 * the value the theme API stores in `assets[slot]`.
 *
 * Returns the presign-provided `public_url` (never a locally-built one) and
 * guards that it is a CDN `game-pass/` URL, so a misconfigured backend surfaces
 * a clear error here instead of a 422 on the subsequent PATCH.
 */
export const uploadGamePassThemeAsset = async ({
  slot,
  file,
}: {
  slot: string;
  file: File;
}): Promise<string> => {
  const key = buildGamePassThemeAssetKey(slot, file);
  const presigned = await presignAndUploadToS3({ key, file });

  const publicUrl = presigned.public_url;
  if (!publicUrl || !THEME_ASSET_URL_PATTERN.test(publicUrl)) {
    throw new Error("Presign did not return a valid game-pass asset URL");
  }
  return publicUrl;
};
